import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { faBooks } from '@fortawesome/pro-solid-svg-icons'
import { RankmiTable } from '@rankmi/rankmi-table'

import { IMainState } from 'store/states'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import { ExternalCourseModelAPI } from 'models/externalCourse.model'
import {
  generateTableLocale,
  useTranslationRankmi,
  joinKeys,
} from 'helpers/translations.helper'
import { canSkipOnRowSelected } from 'helpers'
import { prefixTranslation } from '../../common'
import {
  useColumns,
  ACTION_COLUMN_WIDTH,
  CHECKBOX_COLUMN_WIDTH,
  NAME_COLUMN_WIDTH,
} from './hooks/useColumns'
import styles from './index.module.scss'

type Id = ExternalCourseModelAPI['id']

type DataColumn = ExternalCourseModelAPI & {
  key: ExternalCourseModelAPI['id']
  name: string
  action: Id
}

interface ITable {
  setPage(page: number): void
  setSearch(text: string): void
  setSort: any
  setPerPage(perPage: number): void
  setSelectedRows(ids: Id[]): void
  selectedRows: Id[]
  setFastFilters(value: number): void
  setAllSelected(state: boolean): void
  onDelete(id: Id): void
  onDownload(id: Id): void
}

const Table: React.FC<ITable> = ({
  setPage,
  setSearch,
  setSort,
  setPerPage,
  setSelectedRows,
  selectedRows,
  setFastFilters,
  setAllSelected,
  onDelete: onDeleteBase,
  onDownload,
}) => {
  const { t } = useTranslation()
  const tRelative = useTranslationRankmi({
    prefix: joinKeys(prefixTranslation, 'list.table.header'),
  })
  const [dataSource, setDataSource] = useState<DataColumn[]>([])
  const [translations, setTranslations] = useState<any>({})
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const { list, total, loading } = useSelector(
    ({ externalCourses }: IMainState) => externalCourses
  )

  useEffect(() => {
    setTranslations(() =>
      generateTableLocale({
        header: {
          rowsCount: tRelative('rowsCount'),
          rowsCount_plural: tRelative('rowsCount_plural'),
        },
      })
    )
  }, [tRelative])

  const [selected, setSelected] = useState<
    undefined | { id: number; name: string }
  >()

  useEffect(() => {
    setDataSource(
      list.map(({ id, name, ...rest }) => ({
        id,
        key: id,
        name,
        ...rest,
        action: id,
      }))
    )
  }, [list])

  const onDelete = (id: number, name: string) => {
    setShowDeleteConfirmationModal(true)
    setSelected({ id, name })
  }

  const columns = useColumns({ onDelete, onDownload })

  const tableScrollX = useMemo(
    () =>
      CHECKBOX_COLUMN_WIDTH +
      columns.reduce((total, column) => total + (column.width || 0), 0),
    [columns]
  )

  const handlePreDelete = ({ id, name }: any) => {
    onDeleteBase(id)
    setShowDeleteConfirmationModal(false)
    setSelected({ id, name })
  }

  return (
    <div
      className={styles.container}
      style={
        {
          '--external-course-name-column-width': `${NAME_COLUMN_WIDTH}px`,
          '--external-course-action-column-width': `${ACTION_COLUMN_WIDTH}px`,
        } as React.CSSProperties
      }
    >
      <RankmiTable
        columnHeaders={columns}
        data={dataSource}
        selectedRows={selectedRows}
        total={total}
        onPaginationChange={(page: any) => setPage(page)}
        onSearch={(text: string) => setSearch(text)}
        onSortChange={(sortData: any) => setSort(sortData)}
        loading={loading}
        onRowsPerPageChange={setPerPage}
        onRowSelected={({
          selectedRowKeys,
          allSelected,
          unselectedRowKeys,
          dispatcher,
        }: any) => {
          if (canSkipOnRowSelected(dispatcher)) return
          setSelectedRows(allSelected ? unselectedRowKeys : selectedRowKeys)
          setAllSelected(allSelected)
        }}
        locale={translations}
        countIcon={faBooks}
        initialRowsPerPage={10}
        simpleSelectAll
        onFastActionSelect={(selected: any) => setFastFilters(selected)}
        responsive
        scroll={{ x: tableScrollX }}
      />
      {showDeleteConfirmationModal && (
        <ConfirmationModal
          show={showDeleteConfirmationModal}
          title=""
          text={t('administration.modal.externalCourseDeleteConfirmation', {
            name: selected && selected.name.trim(),
          })}
          acceptButtonText={t('components.modal.buttons.delete')}
          onAccept={() => handlePreDelete(selected && selected)}
          onClose={() => setShowDeleteConfirmationModal(false)}
        />
      )}
    </div>
  )
}

export default Table
